import { useState, useEffect } from 'react';
import { IncompleteRunNotice } from '@/app/components/shared/incomplete-run-notice';
import { AIAssistantSidebar } from '@/app/components/ai-assistant-sidebar';
import { Sparkles, CheckCircle2, Aperture } from 'lucide-react';
import { listDeepSignalRuns, getDeepSignalData } from '@/api/deep-signal-api';
import { isSeriesPoint } from '@/app/services/analytics/deep-signal-series-api';
import type { DeepSignalData, DSRun } from '@/api/deep-signal-api';
import { resolveBrandCustomer } from '@/app/services/brand/resolve-customer';
import type { DeepSignalPageProps, TabId } from './types';
import { DeepSignalCtx } from './context';
import { resolveCustomerContent } from './deep-signal-content';
import { buildFunnelStatsFromApi, buildFunnelStatsFromHeadline, buildMeasurementSummary, variableDisplay } from './builders';
import { BeliefPage } from './belief/belief-page';
import { HeroMetricsRow } from './components/hero-metrics-row';
import { KeyInsightsPanel } from './components/key-insights-panel';
import { RunSelector } from './components/run-selector';
import { getModelLanes } from './model-lanes';
import { getModelLabel, isExcludedFromAnalytics } from '@/app/constants/models';
import { SignalAnalystInput, SignalAnalystTiles } from './components/signal-analyst';
import { DSLockedState } from './components/locked-state';
import { VariableIntelligenceSection } from './sections/variable-intelligence-section';
import { ModelBreakdownSection } from './sections/model-breakdown-section';
import { PersonasSection } from './sections/personas-section';
import { CompetitiveSection } from './sections/competitive-section';
import { WhatAiSaysSection } from './sections/what-ai-says-section';
import { BlueprintSection } from './sections/blueprint-section';
import { ActionListSection } from './sections/action-list-section';
import { PublicationsSection } from './sections/publications-section';
import { TruthSetPanel } from '../truth-set/truth-set-panel';
import { DriftStrip } from './components/drift-strip';
import { FlaggedGapsSection } from './sections/flagged-gaps-section';
import { RebaselineNotice } from '@/app/components/shared/rebaseline-notice';

/** The run the selected run's hero deltas compare against. On the original
 *  series segment that is the baseline-labelled run, as before; after a series
 *  rebaseline it is that segment's NEW baseline, so a delta never spans the
 *  rebaseline. The new baseline itself has nothing to compare against. */
function comparisonBaselineFor(runs: DSRun[], selected: DSRun | undefined): DSRun | undefined {
  const segment = selected?.series?.segment ?? 0;
  if (segment > 0) {
    return runs.find(r => r.series?.new_baseline && r.series.rebaseline_id === selected?.series?.rebaseline_id);
  }
  return runs.find(r => r.run_label === 'baseline' && (r.series?.segment ?? 0) === 0);
}

export function DeepSignalPage({ isSidebarCollapsed, onSidebarToggle, brandName, initialTab }: DeepSignalPageProps) {
  const [activeTab, setActiveTab] = useState<TabId>('variable-intelligence');

  // Deep-link: when navigated here with a target tab (e.g. the Brand Monitoring
  // Blueprint hand-off card passes 'blueprint'), open that tab.
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab as TabId);
  }, [initialTab]);
  
  // Greeting name from the logged-in user (auth full_name), first name only,
  // with a no-name fallback. Self-contained so this branch needs nothing else.
  const firstName = (
    typeof window !== 'undefined'
      ? (localStorage.getItem('full_name') || sessionStorage.getItem('full_name') || '')
      : ''
  ).trim().split(/\s+/)[0];
  
  const [pendingQuestion, setPendingQuestion] = useState<{ text: string; ts: number } | undefined>(undefined);
  // The belief page (one belief across the series) replaces the run view
  // below the page header while open. Cleared when the brand changes.
  const [openBeliefId, setOpenBeliefId] = useState<string | null>(null);

  // ── API data ──
  const [apiData, setApiData]     = useState<DeepSignalData | null>(null);
  const [dsLoading, setDsLoading] = useState(true);
  const [dsError, setDsError]     = useState<string | null>(null);

  // ── Run selection (multi-run aware) ──
  const [customerId, setCustomerId]       = useState<number | null>(null);
  const [runs, setRuns]                   = useState<DSRun[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  // Bumped to force Effect B to re-fetch the current run payload in place (e.g.
  // after an admin resets curated Key Insights, so the restored default shows).
  const [reloadNonce, setReloadNonce]     = useState(0);
  const [baselineStats, setBaselineStats] = useState<ReturnType<typeof buildFunnelStatsFromApi> | null>(null);

  // Effect A: resolve the brand to a customer and load its list of runs.
  // Defaults the selected run to the latest (highest sequence number), so the
  // page opens on the most recent run but every prior run stays reachable.
  useEffect(() => {
    let cancelled = false;
    setApiData(null);
    setRuns([]);
    setCustomerId(null);
    setSelectedRunId(null);
    setOpenBeliefId(null);
    setDsError(null);
    setDsLoading(true);
    // No brand selected → nothing to load. Never fall through to another
    // client's data.
    if (!brandName) { setDsLoading(false); return; }
    // Brand → customer goes through the one shared resolver. The local
    // bidirectional `includes()` this replaced could pick any customer whose
    // name merely contained the brand, including an internal one carrying
    // another client's framework.
    resolveBrandCustomer(brandName)
      .then(match => {
        if (cancelled) return;
        if (!match) { setDsLoading(false); return; }
        setCustomerId(match.id);
        return listDeepSignalRuns(match.id).then(r => {
          if (cancelled) return;
          // The server already returns only series-eligible runs (#120); the
          // same verdict is applied here so a bundle talking to an older
          // backend still never offers a run the Performance chart hides.
          const sorted = r.runs.filter(isSeriesPoint).sort(
            (a, b) => (b.run_sequence_number ?? 0) - (a.run_sequence_number ?? 0)
          );
          setRuns(sorted);
          const latest = sorted[0]?.id ?? match.latest_run_id ?? null;
          setSelectedRunId(latest);
          if (latest == null) setDsLoading(false);
        });
      })
      .catch(err => {
        if (!cancelled) { setDsError(err.message); setDsLoading(false); }
      });
    return () => { cancelled = true; };
  }, [brandName]);

  // Effect B: load the selected run's full payload. Re-runs whenever the user
  // switches runs in the selector.
  useEffect(() => {
    if (customerId == null || selectedRunId == null) return;
    let cancelled = false;
    setApiData(null);
    setDsError(null);
    setDsLoading(true);
    getDeepSignalData(customerId, selectedRunId)
      .then(data => {
        if (cancelled) return;
        setApiData(data);
        setDsLoading(false);
      })
      .catch(err => {
        if (!cancelled) { setDsError(err.message); setDsLoading(false); }
      });
    return () => { cancelled = true; };
  }, [customerId, selectedRunId, reloadNonce]);

  // Effect C: load the baseline run's funnel numbers so re-measurement cards can
  // show change-vs-baseline. Cleared when the selected run IS the baseline.
  useEffect(() => {
    if (customerId == null || runs.length === 0) { setBaselineStats(null); return; }
    const baselineRun = comparisonBaselineFor(runs, runs.find(r => r.id === selectedRunId));
    if (!baselineRun || baselineRun.id === selectedRunId) { setBaselineStats(null); return; }
    // Prefer the baseline run's STORED report headline — the same field Compare
    // Runs reads (baseline_report_json.headline). That is the original scored
    // pass over all calls, so the "vs baseline" delta matches the report exactly
    // instead of re-deriving the baseline from the live rows (which, after a
    // re-score, no longer hold the original pass).
    if (baselineRun.headline_metrics) {
      setBaselineStats(buildFunnelStatsFromHeadline(baselineRun.headline_metrics));
      return;
    }
    // Fallback: a run with no stored report yet. Live-aggregate its rows.
    let cancelled = false;
    getDeepSignalData(customerId, baselineRun.id)
      .then(d => { if (!cancelled) setBaselineStats(buildFunnelStatsFromApi(d)); })
      .catch(() => { if (!cancelled) setBaselineStats(null); });
    return () => { cancelled = true; };
  }, [customerId, runs, selectedRunId]);

  const funnelStats = apiData ? buildFunnelStatsFromApi(apiData) : null;

  // Lanes this client measured that do NOT feed the hero numbers, so the cards
  // can say so out loud. Only lanes that actually RENDER are listed: a lane the
  // catalog hides from Deep Signal entirely (gptweb) is not something the
  // client can see, so naming it would raise a question instead of answering
  // one. What is left is the real case: a lane added after their baseline.
  const heroExcludedLanes = (apiData?.blend_excluded_models ?? [])
    .filter(id => !isExcludedFromAnalytics(id))
    .map(id => ({ id, label: getModelLabel(id) }));
  const selectedRun = runs.find(r => r.id === selectedRunId);
  // A series rebaseline's first read is a baseline too: the new one.
  const selectedSeries = selectedRun?.series ?? apiData?.run.series ?? null;
  const isNewBaseline = Boolean(selectedSeries?.new_baseline);
  const isBaselineRun = selectedRun?.run_label === 'baseline' || isNewBaseline;
  // The rebaseline that opened the selected run's segment, for the banner.
  const segmentRebaseline = selectedSeries && selectedSeries.segment > 0
    ? (apiData?.rebaselines ?? [])[selectedSeries.segment - 1] ?? null
    : null;

  // Run chip for the hero metric row: "{Brand} · Baseline · {month year}" on
  // the baseline run, "Re-measurement" after a refresh run.
  const runDateRaw = apiData?.run?.created_at;
  const runMonthYear = runDateRaw
    ? new Date(runDateRaw).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';
  const runChipLabel = [
    apiData?.customer.brand_name ?? 'Brand',
    isNewBaseline ? 'New baseline' : isBaselineRun ? 'Baseline' : 'Re-measurement',
    runMonthYear,
  ].filter(Boolean).join(' · ');

  // Name the "vs baseline" comparison on the hero deltas with the baseline
  // run's date (e.g. "vs baseline, May 23") so the delta states what it's
  // measured against instead of the vague "since last measurement". After a
  // series rebaseline the comparison is against the NEW baseline.
  const baselineRun = comparisonBaselineFor(runs, selectedRun);
  const baselineDateLabel = baselineRun?.created_at
    ? new Date(baselineRun.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const baselineWord = baselineRun?.series?.new_baseline ? 'new baseline' : 'baseline';
  const comparisonLabel = baselineDateLabel ? `vs ${baselineWord}, ${baselineDateLabel}` : `vs ${baselineWord}`;

  // Per-customer managed-service content (Key Insights + Blueprint). Resolved
  // from the backend payload when present, else the per-customer config, else
  // safe-empty. This is what prevents one client's content leaking into another.
  const content = resolveCustomerContent(apiData);
  const blueprintAvailable = content.blueprintEnabled && !!content.blueprint;

  // The Blueprint tab only exists for customers it is enabled for. If the user
  // is on the Blueprint tab and then switches to a customer without one, fall
  // back to the first tab so we never render an orphaned/empty Blueprint.
  //
  // Gate on apiData being loaded: navigating in from the Blueprint hand-off card
  // sets activeTab='blueprint' while apiData is still null, at which point
  // blueprintAvailable is false simply because the data hasn't arrived yet.
  // Without this guard, that race bounced the user to Brand Attribute Recall
  // before the blueprint could resolve.
  useEffect(() => {
    if (apiData && activeTab === 'blueprint' && !blueprintAvailable) {
      setActiveTab('variable-intelligence');
    }
  }, [apiData, activeTab, blueprintAvailable]);

  const tabs: { id: TabId; label: string }[] = [
    { id: 'variable-intelligence', label: 'Brand Attribute Recall' },
    { id: 'model-breakdown', label: 'Model Breakdown' },
    { id: 'personas', label: 'Personas' },
    { id: 'competitive', label: 'Competitive' },
    { id: 'what-ai-says', label: 'What AI Says' },
    { id: 'actions', label: 'Actions' },
    { id: 'publications', label: 'Publications' },
    { id: 'facts', label: 'Facts' },
    ...(blueprintAvailable ? [{ id: 'blueprint' as TabId, label: 'Blueprint' }] : []),
  ];

  // No run data available (including fetch errors, where apiData stays null):
  // show the clean locked state instead of a half-populated page.
  if (!dsLoading && !apiData) {
    return <DSLockedState />;
  }

  return (
  <DeepSignalCtx.Provider value={{ apiData, loading: dsLoading, error: dsError }}>
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
    <div
      className={`flex-1 transition-all duration-300 ${isSidebarCollapsed ? 'mr-16' : 'mr-[380px]'}`}
      style={{
        minWidth: 0,
        // When the assistant is collapsed it shows as a floating bottom-right
        // launcher (~210px wide, ~120px tall incl. the hint pill) that overlays
        // this column. Reserve a bottom-right gap so the last section's cards
        // scroll clear of it instead of being hidden behind it.
        paddingBottom: isSidebarCollapsed ? '160px' : '120px',
        scrollPaddingBottom: isSidebarCollapsed ? '160px' : '120px',
      }}
    >
      {/* Standard page header */}
      <div className="px-8 md:px-16 py-6 md:py-8 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}>
        <div className="flex items-center gap-2 mb-4 md:mb-6">
          <Aperture className="w-5 h-5 shrink-0" style={{ color: 'var(--muted-foreground)' }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', fontWeight: 'var(--font-weight-medium)' }}>
            Deep Signal
          </span>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <h2 style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--foreground)',
            lineHeight: '1.3',
          }}>
            Comprehensive AI reasoning signal. Deep strategic outcomes.
          </h2>
        </div>

        <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-base)', marginBottom: '20px' }}>
          A custom-scored measurement run across every query, model, persona, and variable. Built for strategic decision-making, not off-the-shelf reporting.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {apiData ? (
            <>
              {/* Brand + run identity. The run label and timestamp come from the
                  selected run via the selector, so they always reflect the real
                  data (no hardcoded "Baseline"). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--foreground)' }}>
                  {apiData.customer.brand_name}
                </span>
                {runs.length > 0 && selectedRunId != null && (
                  <RunSelector runs={runs} selectedRunId={selectedRunId} onSelect={setSelectedRunId} />
                )}
              </div>

              {/* Scope: rendered from whatever models this run actually contains,
                  so the row adapts to any model stack (3, 5, gptweb, etc.). */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <CheckCircle2 size={14} style={{ color: '#12b76a', flexShrink: 0 }} />
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', fontWeight: 500 }}>
                  {Object.keys(apiData.model_breakdown).length}
                </span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', marginRight: '6px' }}>
                  {Object.keys(apiData.model_breakdown).length === 1 ? 'model analyzed' : 'models analyzed'}
                </span>
                {getModelLanes(Object.keys(apiData.model_breakdown)).map(lane => (
                  <span key={lane.id} style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--muted-foreground)',
                    border: '1px solid var(--border)',
                    borderRadius: '99px',
                    padding: '2px 10px',
                    backgroundColor: 'var(--background)',
                  }}>
                    {lane.label}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>
              {dsLoading ? 'Loading…' : 'No data available.'}
            </span>
          )}
        </div>
      </div>

      {openBeliefId && customerId != null ? (
        <BeliefPage
          customerId={customerId}
          beliefId={openBeliefId}
          onBack={() => setOpenBeliefId(null)}
          beliefNames={Object.fromEntries((apiData?.variables ?? []).map(v => [v.id, variableDisplay(v).name]))}
        />
      ) : (<>
      {/* Recommendation Funnel: hero metric row, three cards + per-model breakdown */}
      <div style={{ backgroundColor: 'var(--background)', borderBottom: '1px solid var(--border)', padding: '26px 64px' }}>
        {dsLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ height: '150px', borderRadius: '12px', backgroundColor: 'var(--muted)', opacity: 0.5 }} />
            ))}
          </div>
        ) : funnelStats && funnelStats.length >= 4 ? (
          <>
          <HeroMetricsRow
            stats={funnelStats}
            baselineStats={baselineStats}
            isBaselineRun={isBaselineRun}
            question={`How often are models recommending ${apiData?.customer.brand_name ?? 'your brand'}?`}
            runChipLabel={runChipLabel}
            comparisonLabel={comparisonLabel}
            excludedLanes={heroExcludedLanes}
            onAskAI={(text) => { setPendingQuestion({ text, ts: Date.now() }); onSidebarToggle(false); }}
          />
          {/* Factual drift beside the headline rates: share of answers that
              contradict the confirmed truth set. Nothing until checked. */}
          <DriftStrip
            check={apiData?.run.truth_check}
            isAdmin={Boolean(apiData?.viewer_is_admin)}
            onOpenGaps={() => setActiveTab('facts')}
          />
          {/* Incomplete run: these rates are over the answered calls only.
              Renders nothing for a complete run. */}
          <IncompleteRunNotice completeness={apiData?.run.completeness} />
          {/* Series rebaseline in force at this run: date, what changed, the
              note. Renders nothing for a run before any rebaseline. */}
          <RebaselineNotice rebaseline={segmentRebaseline} newBaseline={isNewBaseline} compact={!isNewBaseline} />
          </>
        ) : (
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)' }}>No funnel data available.</p>
        )}
      </div>

      {/* ── Signal Analyst Wrapper ── */}
      <div style={{ backgroundColor: 'rgba(126,95,224,0.04)', borderBottom: '1px solid var(--border)', padding: '28px 64px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Wrapper header */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '6px' }}>
            <Sparkles size={13} color="#7e5fe0" />
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: '#7e5fe0' }}>Signal Analyst</span>
          </div>
          <p style={{ fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--foreground)', lineHeight: '1.35' }}>
            {firstName ? `Hello, ${firstName}. ` : ''}Here's your Signal Analyst briefing{apiData ? ` for ${apiData.customer.brand_name}` : ''}.
          </p>
        </div>

        {/* ── Subsection 1: Measurement Summary ── */}
        <div style={{
          border: '1px solid var(--border)',
          borderLeft: '3px solid #00c9c4',
          borderRadius: '10px',
          padding: '18px 20px',
          backgroundColor: 'var(--muted)',
        }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: '#00c9c4', marginBottom: '8px' }}>Measurement Summary</p>
          {dsLoading ? (
            <div style={{ height: '40px', borderRadius: '4px', backgroundColor: 'var(--border)', opacity: 0.5 }} />
          ) : apiData ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', lineHeight: '1.65' }}>
              {buildMeasurementSummary(apiData)}
            </p>
          ) : (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.65' }}>
              No measurement data loaded.
            </p>
          )}
        </div>

        {/* ── Subsection 2: Key Insights (hidden when none, unless admin) ── */}
        <KeyInsightsPanel
          insights={content.keyInsights}
          customerId={customerId}
          isAdmin={Boolean(apiData?.viewer_is_admin)}
          isCurated={Boolean(apiData?.key_insights_is_curated)}
          onAskAll={() => {
            setPendingQuestion({
              text: 'Across all key insights from this run, what are the highest-priority actions I should take, and in what order?',
              ts: Date.now(),
            });
            onSidebarToggle(false);
          }}
          onAskAI={(q) => {
            setPendingQuestion({ text: q, ts: Date.now() });
            onSidebarToggle(false);
          }}
          onReload={() => setReloadNonce(n => n + 1)}
        />

        {/* ── Subsection 3: Agent Interface ── */}
        <div style={{
          border: '1px solid var(--border)',
          borderRadius: '10px',
          padding: '20px',
          backgroundColor: 'var(--background)',
        }}>
          <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--spectral-violet)', marginBottom: '14px' }}>Put me to work. What can I solve for you?</p>

          {/* Input bar */}
          <SignalAnalystInput onSubmit={(q) => {
            setPendingQuestion({ text: q, ts: Date.now() });
            onSidebarToggle(false);
          }} />

          {/* Dynamic tiles */}
          {apiData && (
            <SignalAnalystTiles
              apiData={apiData}
              onTileClick={(q) => {
                setPendingQuestion({ text: q, ts: Date.now() });
                onSidebarToggle(false);
              }}
            />
          )}
        </div>

      </div>

      {/* Breaker: sets up what the tabs below are for */}
      <div style={{
        backgroundColor: 'var(--muted)',
        borderTop: '1px solid var(--border)',
        borderBottom: '1px solid var(--border)',
        padding: '24px 64px',
      }}>
        <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, letterSpacing: '0.5px', color: 'var(--muted-foreground)', marginBottom: '8px' }}>
          The Full Picture
        </p>
        <p style={{ fontSize: 'var(--text-3xl)', fontWeight: 700, color: 'var(--foreground)', marginBottom: '8px', whiteSpace: 'nowrap' }}>
          Six lenses on this run.
        </p>
        <p style={{ fontSize: 'var(--text-base)', color: 'var(--muted-foreground)' }}>
          How models understand {apiData?.customer.brand_name ?? 'your brand'} today, and what we're going to do about it.
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{
        backgroundColor: 'var(--background)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: '0',
        zIndex: 10,
      }}>
        <style>{`
          .ds-tab-btn {
            position: relative;
            border: none;
            background: transparent;
            cursor: pointer;
            white-space: nowrap;
            padding: 24px 0 16px;
            font-size: var(--text-lg);
            font-weight: var(--font-weight-semibold);
            color: var(--muted-foreground);
            transition: color 0.2s;
          }
          .ds-tab-btn.ds-tab-active {
            color: var(--foreground);
            font-weight: var(--font-weight-bold);
          }
          .ds-tab-btn.ds-tab-active::after {
            content: '';
            position: absolute;
            bottom: -1px;
            left: 0;
            right: 0;
            height: 4px;
            background-color: var(--alpha-teal);
          }
          /* Tabs stay horizontally scrollable when the content area is narrow,
             but the scrollbar itself is hidden (scroll via trackpad/shift-wheel). */
          .ds-tab-nav {
            -ms-overflow-style: none;   /* IE/Edge */
            scrollbar-width: none;      /* Firefox */
          }
          .ds-tab-nav::-webkit-scrollbar {
            display: none;              /* Chrome/Safari */
          }
        `}</style>
        <div className="ds-tab-nav" style={{ display: 'flex', padding: '0 64px', gap: '32px', alignItems: 'center', overflowX: 'auto' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`ds-tab-btn${activeTab === tab.id ? ' ds-tab-active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div style={{
        backgroundColor: 'var(--background)',
        ...(activeTab === 'what-ai-says' ? {
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 180px)',
          overflow: 'hidden',
        } : {}),
      }}>
        {activeTab === 'variable-intelligence' && (
          <VariableIntelligenceSection
            onAskAI={(q) => {
              setPendingQuestion({ text: q, ts: Date.now() });
              onSidebarToggle(false);
            }}
            onOpenBelief={(id) => { setOpenBeliefId(id); window.scrollTo({ top: 0 }); }}
          />
        )}
        {activeTab === 'model-breakdown' && <ModelBreakdownSection />}
        {activeTab === 'personas' && <PersonasSection />}
        {activeTab === 'competitive' && <CompetitiveSection />}
        {activeTab === 'what-ai-says' && <WhatAiSaysSection />}
        {activeTab === 'blueprint' && blueprintAvailable && <BlueprintSection />}
        {activeTab === 'facts' && customerId != null && (
          // The selected read's flagged gaps, then the brand's truth set: what
          // the client confirms is true, and what every read is checked against.
          <>
            <FlaggedGapsSection
              customerId={customerId}
              runId={selectedRunId}
              check={apiData?.run.truth_check}
              isAdmin={Boolean(apiData?.viewer_is_admin)}
              onChanged={() => setReloadNonce(n => n + 1)}
            />
            <TruthSetPanel customerId={customerId} embedded />
          </>
        )}
        {activeTab === 'publications' && customerId != null && (
          <PublicationsSection customerId={customerId} isAdmin={Boolean(apiData?.viewer_is_admin)} />
        )}
        {activeTab === 'actions' && (
          <ActionListSection
            customerId={customerId}
            onOpenRead={(runId) => {
              if (!runs.some(r => r.id === runId)) return;
              setSelectedRunId(runId);
              setActiveTab('variable-intelligence');
              window.scrollTo({ top: 0 });
            }}
          />
        )}
      </div>
      </>)}
    </div>

    <AIAssistantSidebar
      phase="deep-signal"
      isCollapsed={isSidebarCollapsed}
      onToggleCollapse={onSidebarToggle}
      externalQuestion={pendingQuestion}
    />
    </div>
  </DeepSignalCtx.Provider>
  );
}
